"use client"

import {
  NO_VALUE,
  SERVICE_METRIC_DEFINITIONS,
  type ServiceMetricKey,
} from "@/lib/conversation-quality/metric-definitions"
import { cn } from "@/lib/utils"

import { DefinedTerm, duration } from "./section-parts"

/**
 * Two sides of one metric, side by side.
 *
 * These medians run from seconds to days, and no shared scale survives that: a linear bar leaves the faster side a
 * hairline, a logarithmic one hides the very ratio the reader came for, and a pie would claim the two share a whole
 * they do not. So the numbers are the picture — the better side large and green beside the multiple it wins by, the
 * other quiet — and the comparison the reader would otherwise do in their head is done for them. Whichever side is
 * ahead is the one marked: a team that answers faster than the agent is the reading that matters most. A count of
 * messages compares by bar instead, where lengths of one size tell the truth, and «fewer» takes the place of
 * «faster».
 */

/** Below this the two sides are the same story, and a line comparing them is noise. */
const CLOSE_ENOUGH = 2

/** Past this the exact multiple says nothing a bound does not. */
const RATIO_CEILING = 1_000

/** Below this a multiple keeps its decimal: «2.5×» is a difference «3×» would overstate. */
const RATIO_DECIMAL_UNDER = 10

export function MetricFaceOff({
  metric,
  leftLabel,
  rightLabel,
  left,
  right,
}: {
  metric: ServiceMetricKey;
  leftLabel: string;
  rightLabel: string;
  left: number | null;
  right: number | null;
}) {
  const { label, definition } = SERVICE_METRIC_DEFINITIONS[metric]
  const sides = [
    { name: leftLabel, value: left },
    { name: rightLabel, value: right },
  ]
  const ahead = leader(sides)

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="text-sm">
        <DefinedTerm label={label} definition={definition} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {sides.map((side) => (
          <div key={side.name} className="min-w-0 space-y-0.5">
            <p className="break-words text-xs text-muted-foreground">{side.name}</p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p
                className={cn(
                  "tabular-nums",
                  ahead?.name === side.name
                    ? "text-2xl font-bold text-green-600 dark:text-green-400"
                    : ahead
                      ? "text-lg font-medium text-muted-foreground"
                      : "text-lg font-semibold"
                )}
              >
                {duration(side.value)}
              </p>
              {ahead?.name === side.name && (
                <span className="rounded-full bg-green-600/10 px-2 py-0.5 text-xs font-semibold text-green-600 dark:text-green-400">
                  {`${formatRatio(ahead.ratio)} faster`}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The side whose number is at least twice the smaller — the shorter wait, the fewer messages — and by how much.
 * Nothing where they are near enough, either is missing, or both are zero («0 < 0 × 2» would call that a gap).
 */
export function leader(sides: { name: string; value: number | null }[]): { name: string; ratio: number } | null {
  const measured = sides.filter((side): side is { name: string; value: number } => Number.isFinite(side.value))
  if (measured.length < 2) return null

  const [ahead, behind] = [...measured].sort((a, b) => a.value - b.value)
  if (ahead.value <= 0 || behind.value < ahead.value * CLOSE_ENOUGH) return null

  return { name: ahead.name, ratio: behind.value / ahead.value }
}

/** Rounded before it is placed, or 9.96 would print «10.0×» and 999.6 the bound itself. */
export function formatRatio(ratio: number): string {
  const rounded = ratio < RATIO_DECIMAL_UNDER ? Math.round(ratio * 10) / 10 : Math.round(ratio)
  if (rounded >= RATIO_CEILING) return `more than ${RATIO_CEILING.toLocaleString()}×`
  return rounded < RATIO_DECIMAL_UNDER ? `${rounded.toFixed(1)}×` : `${rounded.toLocaleString()}×`
}

/** Where the two sides are of one size, their lengths compare honestly — and only there. */
export function PairedBars({
  metric,
  leftLabel,
  rightLabel,
  left,
  right,
  format,
}: {
  metric: ServiceMetricKey;
  leftLabel: string;
  rightLabel: string;
  left: number | null;
  right: number | null;
  format: (value: number) => string;
}) {
  const { label, definition, direction } = SERVICE_METRIC_DEFINITIONS[metric]
  const sides = [
    { name: leftLabel, value: left },
    { name: rightLabel, value: right },
  ]
  const widest = Math.max(left ?? 0, right ?? 0)
  const ahead = leader(sides)

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="space-y-0.5">
        <div className="text-sm">
          <DefinedTerm label={label} definition={definition} />
        </div>
        {direction && <p className="text-xs text-muted-foreground">{direction}</p>}
      </div>
      {sides.map((side) => (
        <div key={side.name} className="flex items-center gap-2 text-xs">
          <span className="w-28 shrink-0 leading-tight text-muted-foreground">{side.name}</span>
          <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
            {side.value !== null && widest > 0 && (
              <div
                className={cn("h-full rounded-full", ahead?.name === side.name ? "bg-green-600" : "bg-foreground/70")}
                style={{ width: `${(side.value / widest) * 100}%` }}
              />
            )}
          </div>
          <span
            className={cn(
              "w-10 shrink-0 text-right font-medium tabular-nums",
              ahead?.name === side.name && "text-green-600 dark:text-green-400"
            )}
          >
            {side.value === null ? NO_VALUE : format(side.value)}
          </span>
        </div>
      ))}
      {ahead && (
        <p className="text-xs text-muted-foreground">{`${formatRatio(ahead.ratio)} fewer — ${ahead.name}`}</p>
      )}
    </div>
  )
}
